import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import MarketingLayout from './components/MarketingLayout'

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
}

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
}

function NotFoundPage() {
  useEffect(() => {
    document.title = 'Page Not Found | SOS SMS'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      "The page you're looking for doesn't exist. Return to SOS SMS to get help with a home service emergency."
    )
  }, [])

  return (
    <MarketingLayout>
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 text-white py-24 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-not-found" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-not-found)" />
          </svg>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeUp} className="text-green-300 font-semibold text-sm uppercase tracking-wider mb-4">
              404 Error
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold leading-tight mb-5">
              Page not found
            </motion.h1>
            <motion.p variants={fadeUp} className="text-green-100 text-lg leading-relaxed max-w-xl mx-auto">
              The page you were looking for doesn&apos;t exist or may have been moved. Let&apos;s get you back to SOS SMS&apos;s homepage.
            </motion.p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full h-12 fill-white" preserveAspectRatio="none">
            <path d="M0,32 C360,60 1080,0 1440,32 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 sm:p-10 text-center"
          >
            <motion.div variants={fadeUp} className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl font-extrabold">404</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">
              We couldn&apos;t find that page
            </motion.h2>
            <motion.p variants={fadeUp} className="text-gray-500 text-lg leading-relaxed max-w-xl mx-auto mb-8">
              Try heading back to the homepage to continue exploring our emergency home services platform.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-white font-bold rounded-2xl hover:bg-green-700 transition-colors shadow-md"
              >
                Back to Homepage
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </MarketingLayout>
  )
}

export default NotFoundPage
